import * as React from 'react';
import {
  Text as ChakraText,
  type TextProps as ChakraTextProps,
} from '@chakra-ui/react';

/**
 * Project Text.
 *
 * All text should be imported from here, not from `@chakra-ui/react`
 * directly (enforced via eslint `no-restricted-imports`).
 *
 * NB-parity defaults (each overridable via props), matching NativeBase's
 * Text baseStyle: `fontSize="sm"` (14px), `fontWeight={300}` (light),
 * `color="secondary.500"`, `lineHeight="1.2em"` (from the web-app theme
 * override), `fontFamily="body"` (Figtree), `my={0}`.
 */
export type TextProps = ChakraTextProps;

export const Text = React.forwardRef<HTMLParagraphElement, TextProps>(
  (
    {
      my = 0,
      fontSize = 'sm',
      // NB web-app theme used 1.2em. Do not use Chakra's `base` token
      // (1.5) — same font-size reads noticeably larger with that leading.
      lineHeight = '1.2em',
      // Design system body copy is Figtree Regular (400). NativeBase defaulted
      // to 300, which renders noticeably lighter than every Figma text style.
      fontWeight = 400,
      color = 'secondary.500',
      // Chakra v3 does not auto-apply the theme body font to Text;
      // without this, index.css's system stack wins and Figtree's
      // tighter metrics are lost (text looks slightly larger).
      fontFamily = 'body',
      ...rest
    },
    ref
  ) => (
    <ChakraText
      ref={ref}
      my={my}
      fontSize={fontSize}
      lineHeight={lineHeight}
      fontWeight={fontWeight}
      color={color}
      fontFamily={fontFamily}
      {...rest}
    />
  )
);
Text.displayName = 'Text';
